import '@/global.css';
import {
  Inter_100Thin,
  Inter_200ExtraLight,
  Inter_300Light,
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
  Inter_800ExtraBold,
  Inter_900Black,
} from '@expo-google-fonts/inter';
import {
  Poppins_100Thin,
  Poppins_200ExtraLight,
  Poppins_300Light,
  Poppins_400Regular,
  Poppins_500Medium,
  Poppins_600SemiBold,
  Poppins_700Bold,
  Poppins_800ExtraBold,
  Poppins_900Black,
} from '@expo-google-fonts/poppins';
import {
  DarkTheme,
  DefaultTheme,
  ThemeProvider as NavigationThemeProvider,
} from '@react-navigation/native';
import { useFonts } from 'expo-font';
import { SplashScreen, Stack } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useColorScheme } from 'nativewind';
import { useLayoutEffect, useState, type ReactNode } from 'react';
import { Platform } from 'react-native';
import 'react-native-reanimated';

import { clearAuthTokens } from '@/modules/api';
import { configureDotLottieWasm } from '@/modules/lottie';
import { OnboardingRoot } from '@/modules/root';
import { OnboardingStepsProvider, RulesEngineProvider } from '@/modules/rulesEngine';
import type { OnboardingConfig } from '@/modules/runtimeConfig';
import { OnboardingThemeProvider } from '@/modules/theme';
import { LoadingScreen } from '@/modules/ui';
import { createVidaVendors, type VidaVendorConfig } from '@/modules/vidaVendors';

/**
 * The leaf `EXPO_PUBLIC_*` values the flow reads, handed to `OnboardingRoot` the same way a
 * consumer hands over its own — `VidaReactNative/src/app/login/_layout.tsx` is the worked
 * example of the consumer side.
 *
 * This app could pass nothing and let each resolver fall through to its env var, which is
 * what it used to do and what `@vidahealth/login`'s standalone SPA still does. Passing
 * explicitly is better on two counts. It puts the flow's environment surface in one readable
 * list instead of scattered across resolver fallbacks. And it makes our own app exercise the
 * code path consumers use — with the fallbacks doing the work, a broken merge or a missing
 * resolver key would ship green and only fail in someone else's build.
 *
 * Read at module scope on purpose: these are `process.env` reads in first-party `src/`, where
 * babel inlines them. That is the opposite of the `getConfig`-at-module-scope trap — this is
 * the side of the boundary where reading env early is correct.
 */
const ONBOARDING_CONFIG: OnboardingConfig = {
  apiBaseUrl: process.env.EXPO_PUBLIC_API_BASE_URL || process.env.EXPO_PUBLIC_B2B_BASE_URL,
  skipLegacySession: process.env.EXPO_PUBLIC_SKIP_LEGACY_SESSION === 'true',
  vidaEnv: process.env.EXPO_PUBLIC_VIDA_ENV,
  streamChatApiKey: process.env.EXPO_PUBLIC_STREAM_CHAT_API_KEY,
  lobPublishableKey: process.env.EXPO_PUBLIC_LOB_PUBLISHABLE_KEY,
  transifexToken: process.env.EXPO_PUBLIC_TRANSIFEX_TOKEN,
};

/**
 * PostHog and Datadog credentials — a **separate object for a separate audience**, and the
 * one place this app declares that it owns those vendors.
 *
 * They are not in `ONBOARDING_CONFIG` because they are not values the flow reads; they are
 * values whoever *constructs* the clients reads, and inside login or VidaReactNative that is
 * the host, against the same PostHog project and the same Datadog org. Those hosts pass their
 * own `analytics` and never import `vidaVendors` at all — which is also how they avoid
 * evaluating six vendor SDKs Metro would otherwise not tree-shake.
 *
 * We are the root, so we build them. See `docs/vendor-ownership-proposal.md`.
 */
const VENDOR_CONFIG: VidaVendorConfig = {
  vidaEnv: process.env.EXPO_PUBLIC_VIDA_ENV,
  posthog: {
    enabled: process.env.EXPO_PUBLIC_POSTHOG_ENABLED === 'true',
    apiKey: process.env.EXPO_PUBLIC_POSTHOG_API_KEY,
    host: process.env.EXPO_PUBLIC_POSTHOG_HOST,
  },
  datadog: {
    enabled: process.env.EXPO_PUBLIC_DATADOG_ENABLED === 'true',
    applicationId: process.env.EXPO_PUBLIC_DATADOG_APPLICATION_ID,
    clientToken: process.env.EXPO_PUBLIC_DATADOG_CLIENT_TOKEN,
    sessionReplayEnabled: process.env.EXPO_PUBLIC_DATADOG_SESSION_REPLAY_ENABLED === 'true',
  },
};

/**
 * Built once, at module scope, and spread into `OnboardingRoot` below.
 *
 * Not inside the component: the returned object carries a `Provider` component, and a fresh
 * component type on every render would remount the whole subtree — taking the Datadog init
 * effect and the PostHog provider with it.
 */
const VENDORS = createVidaVendors(VENDOR_CONFIG);

/**
 * What a dead session means **here**: destroy it.
 *
 * This app is the only party in its own process, so the tokens in SecureStore are nobody
 * else's and clearing them is the whole correct response — a stale `vida_jwt_token` left behind
 * would be re-read on the next launch and fail again.
 *
 * It has to be stated rather than assumed, because `OnboardingRoot` clears nothing by default.
 * That default protects the embedded case, where the same keys carry the host app's session
 * (see `onAuthFailure`), and it means the party who has to remember something is us — which is
 * the right way round, and has the side benefit that our own app exercises the port a consumer
 * uses instead of a built-in special case.
 *
 * `reason` is ignored: all three reasons mean the same thing when you own the only session in
 * the process. A host is where telling them apart earns its keep.
 */
const clearVidaSession = () => clearAuthTokens();

SplashScreen.preventAutoHideAsync();

// Point the dotLottie web player at our self-hosted WASM before any screen (and
// thus any LottieView) mounts — see src/modules/lottie. No-op on native.
configureDotLottieWasm();

export default function RootLayout() {
  const [isWebMounted, setIsWebMounted] = useState(Platform.OS !== 'web');
  const [fontsLoaded] = useFonts({
    Inter_100Thin,
    Inter_200ExtraLight,
    Inter_300Light,
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    Inter_800ExtraBold,
    Inter_900Black,
    Poppins_100Thin,
    Poppins_200ExtraLight,
    Poppins_300Light,
    Poppins_400Regular,
    Poppins_500Medium,
    Poppins_600SemiBold,
    Poppins_700Bold,
    Poppins_800ExtraBold,
    Poppins_900Black,
  });

  useLayoutEffect(() => {
    if (!isWebMounted && Platform.OS === 'web') {
      setIsWebMounted(true);
      return;
    }

    if (isWebMounted && fontsLoaded) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded, isWebMounted]);

  if (Platform.OS === 'web') {
    if (!isWebMounted) {
      return <LoadingScreen />;
    }

    if (!fontsLoaded) {
      return <ThemedLoadingScreen />;
    }
  } else if (!fontsLoaded) {
    return <ThemedLoadingScreen />;
  }

  // `OnboardingRoot` is outermost. It publishes the leaf config, publishes the analytics
  // port it was handed, and initializes the scoped `onboardingTx` instance — which is why
  // there is no `tx.init` in this file any more.
  //
  // Spreading `VENDORS` is this app declaring that it owns PostHog and Datadog: it supplies
  // the port, the vendor providers, and `trackScreens: true`. A host app passes its own
  // `analytics` here instead and spreads nothing — same prop, opposite owner, no flag
  // anywhere. See `docs/vendor-ownership-proposal.md`.
  //
  // `config` is passed explicitly even though this app could rely on the env fallbacks; see
  // `ONBOARDING_CONFIG` above. No `locale`, so the flow follows the device.
  //
  // The translation readiness gate lives in the provider, along with the Transifex instance
  // it gates on. The fonts gate above stays here, since it is the app's concern, not the
  // package's — but the two now interact: `SplashScreen.hideAsync()` fires as soon as fonts
  // load, so on a Spanish device the splash goes away while the CDS fetch is still in flight.
  // `fallback` is what fills that window; without it the gate renders nothing and the user
  // watches a blank, unthemed screen. `ThemedLoadingScreen` is the same thing this file shows
  // while fonts load, so the two waits look like one.
  //
  // `onAuthFailure` is this app claiming the session, the same way spreading `VENDORS` claims
  // PostHog and Datadog. A host passes its own handler or none at all — see `clearVidaSession`.
  //
  // The rest of the stack stays app-owned for now: a consumer mounts only the
  // dynamic-assessment screen today, so nothing yet needs the rules engine or our theme from
  // a shared root. That composition moves into `@/modules/root` when the onboarding steps are
  // exposed.
  return (
    <OnboardingRoot
      config={ONBOARDING_CONFIG}
      onAuthFailure={clearVidaSession}
      fallback={<ThemedLoadingScreen />}
      {...VENDORS}
    >
      <AppThemeProvider>
        <RulesEngineProvider>
          <OnboardingStepsProvider>
            <Stack screenOptions={{ headerShown: false }} />
            <StatusBar style='auto' />
          </OnboardingStepsProvider>
        </RulesEngineProvider>
      </AppThemeProvider>
    </OnboardingRoot>
  );
}

/**
 * This app's theme root: the published `OnboardingThemeProvider` plus the navigation chrome
 * only this app has.
 *
 * Local to this file rather than a `src/` component, because it is composition for exactly one
 * app — the same reason `ThemedLoadingScreen` below is local. Only a file's default export is a
 * route, so a named component here is not one.
 *
 * The navigation half cannot move into `modules/theme`: that module ships as
 * `@vidahealth/onboarding/theme`, and `@react-navigation/native` is a `devDependency`. Metro
 * does not tree-shake, so a consumer importing the subpath for `OnboardingThemeProvider` would
 * have evaluated the react-navigation import too and failed with `Cannot find module` without
 * ever rendering it.
 *
 * What the navigation theme still does, with `headerShown: false` on both `Stack`s and every
 * `Screen` painting its own `bg-background-0`, is the navigator's scene background during a
 * push/pop transition. Reads nativewind's colour scheme rather than a value threaded out of
 * `OnboardingThemeProvider`: that provider is the thing that *sets* it — explicitly on web,
 * where the production export otherwise leaves it at `light` — so nativewind is already
 * authoritative.
 */
function AppThemeProvider({ children }: { children: ReactNode }) {
  const { colorScheme } = useColorScheme();

  return (
    <OnboardingThemeProvider>
      <NavigationThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
        {children}
      </NavigationThemeProvider>
    </OnboardingThemeProvider>
  );
}

function ThemedLoadingScreen() {
  return (
    <AppThemeProvider>
      <LoadingScreen />
    </AppThemeProvider>
  );
}
